import { useCallback } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import invariant from 'tiny-invariant'
import { TextFieldInput, GridItem } from '@sequencehq/core-components'
import {
  Controls,
  FieldGrid,
  FieldLabel,
  SecondaryOptions,
  Title
} from '../../common/layout'
import {
  DescriptionField,
  LedgerIntegrationFields,
  RecognitionMethodField,
  RevenueClassificationField,
  ServicePeriodField,
  TaxCategoryField
} from '../../common/fields'
import {
  descriptionAtom,
  descriptionEnabledAtom,
  titleAtom,
  revenueRecognitionMethodAtom,
  servicePeriodAtom,
  taxCategoryIdAtom,
  formStateAtom,
  externalIdsAtom,
  externalIdsEditableAtom,
  revenueClassificationAtom
} from './state.atoms'
import { validateFormAtom, validationResultsAtom } from './validation.atoms'
import { lineItemGroupsAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { formStateToLineItemGroup, lineItemGroupToFormState } from './adapters'
import { BaseConfig, EditLineItemGroupConfig } from '../../types'
import { isFieldInvalid } from '../../common/validation'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import { TaxRateModals, useClientInvoiceTaxModals } from 'Invoices/components'

export const EditLineItemGroup = (
  props: EditLineItemGroupConfig & BaseConfig
) => {
  const [lineItemGroups, setLineItemGroups] = useAtom(lineItemGroupsAtom)

  const lineItemGroup = lineItemGroups?.find(
    group => group.id === props.lineItemGroupId
  )

  invariant(lineItemGroup, 'Line item group not found')

  useHydrateAtoms([[formStateAtom, lineItemGroupToFormState(lineItemGroup)]])

  const [title, setTitle] = useAtom(titleAtom)
  const [servicePeriod, setServicePeriod] = useAtom(servicePeriodAtom)
  const [descriptionEnabled, setDescriptionEnabled] = useAtom(
    descriptionEnabledAtom
  )
  const [description, setDescription] = useAtom(descriptionAtom)
  const [taxCategoryId, setTaxCategoryId] = useAtom(taxCategoryIdAtom)
  const [revenueRecognitionMethod, setRevenueRecognitionMethod] = useAtom(
    revenueRecognitionMethodAtom
  )
  const [revenueClassification, setRevenueClassification] = useAtom(
    revenueClassificationAtom
  )
  const [externalIds, setExternalIds] = useAtom(externalIdsAtom)
  const externalIdsEditable = useAtomValue(externalIdsEditableAtom)

  const validationResults = useAtomValue(validationResultsAtom)
  const validateForm = useSetAtom(validateFormAtom)
  const formState = useAtomValue(formStateAtom)
  const [secondaryOptionsVisible, setSecondaryOptionsVisible] = useAtom(
    secondaryOptionsVisibleAtom
  )

  const taxModalsHook = useClientInvoiceTaxModals({ taxCategoryId })

  const onUpdate = useCallback(() => {
    const result = validateForm()

    if (!result || !lineItemGroups) return

    taxModalsHook.functions.onConfirmSubmit(() => {
      setLineItemGroups(
        lineItemGroups.map(group => {
          if (group.id !== lineItemGroup.id) return group
          return formStateToLineItemGroup({ formState, lineItemGroup })
        })
      )

      props.onDismiss()
    })()
  }, [
    taxModalsHook.functions,
    props.onDismiss,
    formState,
    lineItemGroup,
    lineItemGroups,
    validateForm,
    setLineItemGroups
  ])

  return (
    <>
      <Title>Edit line item</Title>

      <FieldGrid>
        <GridItem gridColumn="span 4">
          <FieldLabel label="Name" validationErrors={validationResults?.title}>
            <TextFieldInput
              placeholder="Name"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
              isInvalid={isFieldInvalid(validationResults?.title)}
              data-1p-ignore
              data-lpignore
            />
          </FieldLabel>
        </GridItem>
      </FieldGrid>

      <SecondaryOptions
        isVisible={secondaryOptionsVisible}
        setIsVisible={setSecondaryOptionsVisible}
      >
        <SecondaryOptions.Group>
          <SecondaryOptions.ToggleField
            label="Description"
            isVisible={descriptionEnabled}
            setIsVisible={setDescriptionEnabled}
          >
            <DescriptionField
              data-testid="lineItemEditor.group.description"
              description={description}
              setDescription={setDescription}
              validationErrors={validationResults?.description}
            />
          </SecondaryOptions.ToggleField>
        </SecondaryOptions.Group>

        <SecondaryOptions.Group>
          <ServicePeriodField
            servicePeriod={servicePeriod}
            setServicePeriod={setServicePeriod}
          />

          <FieldLabel label="Tax category">
            <TaxCategoryField
              taxCategoryId={taxCategoryId}
              setTaxCategoryId={setTaxCategoryId}
              isInvalid={isFieldInvalid(validationResults?.taxCategoryId)}
            />
          </FieldLabel>

          <LedgerIntegrationFields
            customerLedgerIntegrations={props.customerLedgerIntegrations}
            externalIds={externalIds}
            setExternalIds={setExternalIds}
            disabled={!externalIdsEditable}
            validationResults={validationResults?.externalIds ?? []}
          />

          <RevenueClassificationField
            revenueClassification={revenueClassification}
            setRevenueClassification={setRevenueClassification}
          />

          <RecognitionMethodField
            revenueRecognitionMethod={revenueRecognitionMethod}
            setRevenueRecognitionMethod={setRevenueRecognitionMethod}
            revenueClassification={revenueClassification}
            servicePeriod={servicePeriod}
          />
        </SecondaryOptions.Group>
      </SecondaryOptions>

      <Controls>
        <Controls.Cancel onClick={props.onDismiss} />
        <Controls.Confirm
          data-testid="lineItemEditor.group.update"
          onClick={onUpdate}
          label="Update"
          keyboardShortcut={['Mod', 'Enter']}
        />
      </Controls>

      <TaxRateModals hook={taxModalsHook} />
    </>
  )
}
